import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz, getTodayInTz } from '../../utils/timezone';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { OrderLineTable, OrderDespatchCard } from '../../components/nursery';
import {
  ShoppingCart, CheckCircle, Package, FileText,
  Calendar, User, Truck, Hash, Landmark, ListPlus, Download, XCircle, Tag,
} from 'lucide-react';
import { CancelOrderDialog } from '../../components/nursery/CancelOrderDialog';
import { RepriceOrderDialog } from '../../components/nursery/RepriceOrderDialog';
import nurseryService from '../../services/nursery';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { PaymentStateBadge } from '../../components/nursery/accounts/AccountBits';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { downloadBlob } from '../../utils/downloadBlob';
import { addDays } from '../../utils/payPeriod';
import { money, num } from '../../utils/nurseryMoney';

const INVOICE_TERMS_DAYS = 30;
import type { NurseryOrder, NurseryOrderStatus, AvailableStock, NurseryInvoice } from '../../types/nursery';
import type { PaymentState } from '../../types/nurseryAccounts';

const PO_MAX = 100;

/** Before it leaves the yard. An invoiced order is credited, not cancelled. */
const CANCELLABLE_STATUSES: NurseryOrderStatus[] = ['draft', 'confirmed', 'partially_allocated', 'allocated', 'picking', 'picked'];

/** Prices can still move until the trees leave, or the invoice is authorised. */
const REPRICEABLE_STATUSES = CANCELLABLE_STATUSES;

/** Paid, part paid and overdue are worked out from the balance and the due date; they are never stored. */
const invoicePaymentState = (inv: NurseryInvoice, today: string): PaymentState => {
  if (inv.status === 'draft') return 'draft';
  if (inv.status === 'cancelled') return 'void';
  if (inv.status === 'paid') return 'paid';
  if (inv.status === 'overdue' || (inv.due_date && inv.due_date.slice(0, 10) < today)) return 'overdue';
  return num(inv.amount_paid) > 0 ? 'part_paid' : 'open';
};

const STATUS_COLORS: Record<NurseryOrderStatus, string> = {
  draft:                'bg-neutral-100 text-neutral-700',
  confirmed:            'bg-blue-100 text-blue-700',
  allocated:            'bg-indigo-100 text-indigo-700',
  partially_allocated:  'bg-amber-100 text-amber-700',
  picking:              'bg-yellow-100 text-yellow-700',
  picked:               'bg-teal-100 text-teal-700',
  shipped:              'bg-cyan-100 text-cyan-700',
  invoiced:             'bg-green-100 text-green-700',
  completed:            'bg-green-100 text-green-800',
  cancelled:            'bg-red-100 text-red-700',
};

function OrderDetail() {
  const timezone = useOrgTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAccess: canManage } = useModuleLicense('nursery', ['manager', 'admin']);
  // The money side is the Accounts module's. Without a seat there, a nursery
  // manager still sees the order's invoices and downloads them.
  const { hasAccess: canAccounts } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: isAccountsAdmin } = useModuleLicense('accounts', 'admin');
  const [pdfBusy, setPdfBusy] = useState<number | null>(null);

  const [order, setOrder] = useState<NurseryOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Allocation modal state
  const [showAllocate, setShowAllocate] = useState(false);
  const [allocLineId, setAllocLineId] = useState<number | null>(null);
  const [availableStock, setAvailableStock] = useState<AvailableStock[] | null>(null);
  const [allocLotId, setAllocLotId] = useState<number | ''>('');
  const [allocQuantity, setAllocQuantity] = useState<number | ''>('');
  const [allocSubmitting, setAllocSubmitting] = useState(false);
  const [allocError, setAllocError] = useState<string | null>(null);

  // Invoice modal state
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  // Purchase order number, edited in place
  const [poDraft, setPoDraft] = useState<string | null>(null);
  const [poSaving, setPoSaving] = useState(false);
  const [poError, setPoError] = useState<string | null>(null);

  const [showCancel, setShowCancel] = useState(false);
  const [showReprice, setShowReprice] = useState(false);
  const [repriceNote, setRepriceNote] = useState<string | null>(null);

  // Refund retry
  const [refundRetrying, setRefundRetrying] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const loadOrder = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getOrder(Number(id));
      if (res.success) {
        setOrder(res.data);
      } else {
        setError('Order not found');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrder(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    if (!order) return;
    setActionLoading(true);
    try {
      await nurseryService.updateOrder(order.id, { status: 'confirmed' } as any);
      loadOrder();
    } catch { /* ignore */ }
    setActionLoading(false);
  };

  const allocLine = order?.lines?.find(l => l.id === allocLineId) ?? null;
  const allocRemaining = allocLine ? Math.max(allocLine.quantity_ordered - allocLine.quantity_allocated, 0) : 0;
  const allocLot = availableStock?.find(s => s.id === allocLotId) ?? null;
  const allocMax = allocLot ? Math.min(allocRemaining, Number(allocLot.available_count)) : allocRemaining;

  const handleOpenAllocate = async (lineId: number) => {
    setAllocLineId(lineId);
    setAllocLotId('');
    setAllocQuantity('');
    setAllocError(null);
    setAvailableStock(null);
    setShowAllocate(true);
    try {
      // By line, so a line sold under an umbrella product is offered the lots
      // of every stock item it may be filled from.
      const res = await nurseryService.getAvailableStock({ orderLineId: lineId });
      setAvailableStock(res.success ? res.data || [] : []);
      if (!res.success) setAllocError(errorMessage(res, 'Could not load available lots'));
    } catch (e) {
      setAvailableStock([]);
      setAllocError(errorMessage(e, 'Could not load available lots'));
    }
  };

  const handleSelectLot = (lotId: number | '') => {
    setAllocLotId(lotId);
    const lot = availableStock?.find(s => s.id === lotId);
    // Offer as much as both the line and the lot can take.
    setAllocQuantity(lot ? Math.min(allocRemaining, Number(lot.available_count)) : '');
  };

  const handleAllocate = async () => {
    if (!allocLineId || !allocLotId || !allocQuantity) return;
    setAllocSubmitting(true);
    setAllocError(null);
    try {
      const res = await nurseryService.allocate({
        order_line_id: allocLineId,
        lot_id: Number(allocLotId),
        quantity: Number(allocQuantity),
      });
      if (res.success) {
        setShowAllocate(false);
        loadOrder();
      } else {
        setAllocError(errorMessage(res, 'Allocation failed'));
      }
    } catch (e) {
      setAllocError(errorMessage(e, 'Allocation failed'));
    }
    setAllocSubmitting(false);
  };

  const handleOpenInvoice = () => {
    // Today in the org's timezone — a UTC date is yesterday before 10 AM in Melbourne.
    const today = getTodayInTz(timezone);
    setInvoiceDate(today);
    setInvoiceDueDate(addDays(today, INVOICE_TERMS_DAYS));
    setInvoiceError(null);
    setShowInvoice(true);
  };

  // The due date follows the invoice date until someone sets it by hand.
  const handleInvoiceDateChange = (value: string) => {
    if (value && invoiceDate && invoiceDueDate === addDays(invoiceDate, INVOICE_TERMS_DAYS)) {
      setInvoiceDueDate(addDays(value, INVOICE_TERMS_DAYS));
    }
    setInvoiceDate(value);
  };

  const handleInvoicePdf = async (inv: NurseryInvoice) => {
    setPdfBusy(inv.id);
    setError(null);
    try {
      downloadBlob(await nurseryAccountsService.getInvoicePdf(inv.id), `${inv.invoice_number}.pdf`);
    } catch (e) {
      setError(errorMessage(e, 'Could not download the invoice'));
    }
    setPdfBusy(null);
  };

  const handleCreateInvoice = async () => {
    if (!order || !invoiceDate) return;
    setInvoiceSubmitting(true);
    setInvoiceError(null);
    try {
      const res = await nurseryService.createInvoice({
        order_id: order.id,
        invoice_date: invoiceDate,
        ...(invoiceDueDate ? { due_date: invoiceDueDate } : {}),
      });
      if (res.success) {
        setShowInvoice(false);
        loadOrder();
      } else {
        setInvoiceError(errorMessage(res, 'Could not create the invoice'));
      }
    } catch (e) {
      setInvoiceError(errorMessage(e, 'Could not create the invoice'));
    }
    setInvoiceSubmitting(false);
  };

  const handleSavePo = async () => {
    if (!order || poDraft === null) return;
    const value = poDraft.trim();
    if (!value && order.account?.po_required) {
      setPoError(`${order.account.payer_name} needs a purchase order number on every order.`);
      return;
    }
    setPoSaving(true);
    setPoError(null);
    try {
      const res = await nurseryService.updateOrder(order.id, { customer_po: value || null });
      if (res.success) {
        setPoDraft(null);
        // The save response carries none of the joined fields.
        loadOrder();
      } else {
        setPoError(errorMessage(res, 'Could not save the PO number'));
      }
    } catch (e) {
      setPoError(errorMessage(e, 'Could not save the PO number'));
    }
    setPoSaving(false);
  };

  const handleRetryRefund = async () => {
    if (!order?.refund) return;
    setRefundRetrying(true);
    setRefundError(null);
    try {
      const res = await nurseryAccountsService.retryRefund(order.refund.id);
      if (res.success) loadOrder();
      else setRefundError(errorMessage(res, 'The refund could not be issued'));
    } catch (e) {
      setRefundError(errorMessage(e, 'The refund could not be issued'));
    }
    setRefundRetrying(false);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-neutral-100 rounded-lg" />
          <div className="h-32 bg-neutral-100 rounded-lg" />
          <div className="h-48 bg-neutral-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 text-center py-16">
        <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
        <p className="text-neutral-500">Order not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/nursery/orders')}>
          Back to Orders
        </Button>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[order.status] || 'bg-neutral-100 text-neutral-700';
  // Orders are invoiced when picked (older website orders at checkout); never offer a second invoice.
  const hasLiveInvoice = (order.invoices ?? []).some(inv => inv.status !== 'cancelled');
  // The PO is printed on an authorised invoice, so it is fixed from then on.
  const poLocked = (order.invoices ?? []).some(inv => inv.status !== 'cancelled' && !!inv.authorised_at);
  const today = getTodayInTz(timezone);
  const account = order.account ?? null;
  const paidByOther = !!account && account.payer_id !== order.customer_id;
  const credit = order.credit ?? null;
  const creditWarning = !!credit && (credit.over_limit || num(credit.overdue_30) > 0);
  const refund = order.refund ?? null;
  // A line entered before its tree had a price. It invoices at nothing, so an
  // invoice carrying one is held until it is priced.
  const unpricedLines = (order.lines ?? []).filter(l => l.unit_price == null);
  const canReprice = canManage && REPRICEABLE_STATUSES.includes(order.status) && !poLocked;

  return (
    <>
      <PageHeader
        title={`Order ${order.order_number}`}
        subtitle={order.customer_name || `Customer #${order.customer_org_id}`}
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${statusColor}`}>
              {order.status.replace(/_/g, ' ').toUpperCase()}
            </span>

            {canManage && ['draft', 'confirmed'].includes(order.status) && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/nursery/orders/new?order=${order.id}`)}>
                <ListPlus className="h-4 w-4" />
                Change quantities
              </Button>
            )}

            {order.status === 'draft' && (
              <Button variant="primary" size="sm" onClick={handleConfirm} disabled={actionLoading}>
                <CheckCircle className="h-4 w-4" />
                {actionLoading ? 'Confirming...' : 'Confirm'}
              </Button>
            )}

            {['confirmed', 'partially_allocated'].includes(order.status) && (
              <Button variant="outline" size="sm" onClick={() => {
                const line = order.lines?.find(l => l.quantity_allocated < l.quantity_ordered);
                if (line) handleOpenAllocate(line.id);
              }}>
                <Package className="h-4 w-4" />
                Allocate
              </Button>
            )}

            {canReprice && (
              <Button variant="outline" size="sm" onClick={() => { setRepriceNote(null); setShowReprice(true); }}>
                <Tag className="h-4 w-4" />
                Update prices
              </Button>
            )}

            {canManage && CANCELLABLE_STATUSES.includes(order.status) && !hasLiveInvoice && (
              <Button variant="outline" size="sm" onClick={() => setShowCancel(true)}>
                <XCircle className="h-4 w-4" />
                Cancel order
              </Button>
            )}

            {canAccounts && ['allocated', 'picked', 'shipped', 'completed', 'invoiced'].includes(order.status) && !hasLiveInvoice && (
              <Button variant="outline" size="sm" onClick={handleOpenInvoice}>
                <FileText className="h-4 w-4" />
                Create Invoice
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {order.status === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">
            Cancelled{order.cancelled_at ? ` on ${formatDateInTz(order.cancelled_at, timezone)}` : ''}.
            {order.cancel_reason ? ` ${order.cancel_reason}` : ''}
          </div>
        )}

        {refund?.status === 'succeeded' && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-900 break-words">
            Refunded {money(refund.amount)} to the customer's card
            {refund.completed_at ? ` on ${formatDateInTz(refund.completed_at, timezone)}` : ''}.
          </div>
        )}
        {refund?.status === 'pending' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            A refund of {money(refund.amount)} is being issued.
          </div>
        )}
        {refund?.status === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            <p className="font-semibold break-words">
              The refund of {money(refund.amount)} did not go through. The customer has not been refunded.
            </p>
            <p className="break-words">{refundError || refund.error || 'The card provider gave no reason.'}</p>
            {isAccountsAdmin && (
              <div className="mt-2">
                <Button variant="outline" size="sm" onClick={handleRetryRefund} disabled={refundRetrying}>
                  {refundRetrying ? 'Trying…' : 'Try again'}
                </Button>
              </div>
            )}
          </div>
        )}
        {!refund && num(order.refund_due) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            {money(order.refund_due)} will be refunded when the load is despatched, for the trees that are not carried
            to a backorder.
          </div>
        )}

        {repriceNote && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-900 break-words">
            {repriceNote}
          </div>
        )}

        {unpricedLines.length > 0 && order.status !== 'cancelled' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            <p className="font-semibold">
              {unpricedLines.length} line{unpricedLines.length === 1 ? ' has' : 's have'} no price.
            </p>
            <p>
              {poLocked
                ? 'The invoice has been authorised, so a credit note or a new invoice in Accounts is the way to bill them.'
                : 'The invoice will not be authorised until they do. Add the price to a price list, then use Update prices.'}
            </p>
            {canReprice && (
              <div className="mt-2">
                <Button variant="outline" size="sm" onClick={() => { setRepriceNote(null); setShowReprice(true); }}>
                  Update prices
                </Button>
              </div>
            )}
          </div>
        )}

        {creditWarning && credit && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            {credit.over_limit && (
              <p>
                <span className="font-semibold">{credit.payer_name} is over its credit limit.</span>{' '}
                It owes {money(credit.balance)} against a limit of {money(credit.credit_limit)}.
              </p>
            )}
            {num(credit.overdue_30) > 0 && (
              <p>
                {credit.over_limit ? 'It also has ' : <span className="font-semibold">{credit.payer_name} has </span>}
                {money(credit.overdue_30)} more than 30 days overdue.
              </p>
            )}
            <p className="text-xs mt-1">This is a warning only. The order can still go ahead.</p>
          </div>
        )}

        {/* Order details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Order Details</h3>

            <DetailRow icon={<Calendar className="h-4 w-4 text-neutral-400" />} label="Order Date">
              {formatLocalDateInTz(order.order_date, timezone)}
            </DetailRow>

            <DetailRow icon={<User className="h-4 w-4 text-neutral-400" />} label="Customer">
              {order.customer_name || `Org #${order.customer_org_id}`}
            </DetailRow>

            {paidByOther && account && (
              <DetailRow icon={<Landmark className="h-4 w-4 text-neutral-400" />} label="Account">
                Paid by {account.payer_name}
              </DetailRow>
            )}

            <DetailRow icon={<Hash className="h-4 w-4 text-neutral-400" />} label="PO number">
              {poDraft === null ? (
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className={order.customer_po ? '' : 'font-normal text-neutral-500'}>
                    {order.customer_po || 'None given'}
                  </span>
                  {canManage && !poLocked && (
                    <button type="button" aria-label="Change the PO number"
                      className="text-xs font-normal text-primary-600 hover:underline"
                      onClick={() => { setPoError(null); setPoDraft(order.customer_po ?? ''); }}>
                      Change
                    </button>
                  )}
                </span>
              ) : (
                <span className="block space-y-2">
                  <label htmlFor="order-po" className="sr-only">Customer PO number</label>
                  <input id="order-po" type="text" maxLength={PO_MAX} value={poDraft}
                    onChange={e => setPoDraft(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <span className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={handleSavePo} disabled={poSaving}>{poSaving ? 'Saving…' : 'Save'}</Button>
                    <Button size="sm" variant="outline" onClick={() => setPoDraft(null)} disabled={poSaving}>Cancel</Button>
                  </span>
                </span>
              )}
              {poError && <span className="block text-xs font-normal text-red-600 mt-1">{poError}</span>}
              {poLocked && (
                <span className="block text-xs font-normal text-neutral-500 mt-1">
                  It is printed on an authorised invoice, so it can no longer be changed here.
                </span>
              )}
              {!poLocked && !order.customer_po && account?.po_required && (
                <span className="block text-xs font-normal text-amber-700 mt-1">
                  {account.payer_name} needs a purchase order number on every order.
                </span>
              )}
            </DetailRow>

            {order.requested_delivery_date && (
              <DetailRow icon={<Truck className="h-4 w-4 text-neutral-400" />} label="Requested">
                {formatLocalDateInTz(order.requested_delivery_date, timezone)}
              </DetailRow>
            )}

            {order.estimated_delivery_date && (
              <DetailRow icon={<Truck className="h-4 w-4 text-neutral-400" />} label="Est. Delivery">
                {formatLocalDateInTz(order.estimated_delivery_date, timezone)}
              </DetailRow>
            )}

            <DetailRow icon={<ShoppingCart className="h-4 w-4 text-neutral-400" />} label="Placed By">
              {order.placed_by_org === 'nursery' ? 'Nursery' : 'Customer'}
            </DetailRow>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Summary</h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-primary-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-primary-700">{order.total_trees.toLocaleString()}</p>
                <p className="text-xs font-medium text-primary-600">Total Trees</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-green-700">
                  ${Number(order.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs font-medium text-green-600">Total Value</p>
              </div>
            </div>

            {order.notes && (
              <div className="pt-2 border-t border-neutral-100">
                <p className="text-xs text-neutral-500 mb-1">Notes</p>
                <p className="text-sm text-neutral-700">{order.notes}</p>
              </div>
            )}
          </div>
        </div>

        <OrderDespatchCard order={order} timezone={timezone} onSaved={loadOrder} />

        {/* Order lines */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">Order Lines</h3>
          {order.lines && order.lines.length > 0 ? (
            <OrderLineTable
              lines={order.lines}
              onAllocate={['confirmed', 'partially_allocated'].includes(order.status) ? handleOpenAllocate : undefined}
            />
          ) : (
            <p className="text-sm text-neutral-400 text-center py-6">No line items</p>
          )}
        </div>

        {/* Invoices */}
        {order.invoices && order.invoices.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">Invoices</h3>
            <div className="space-y-2">
              {order.invoices.map(inv => {
                const balance = num(inv.total_amount) - num(inv.amount_paid) - num(inv.amount_credited);
                const isVoid = inv.status === 'cancelled';
                const body = (
                  <>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-primary-700 break-words">{inv.invoice_number}</p>
                      <p className="text-xs text-neutral-500">
                        {formatLocalDateInTz(inv.invoice_date.slice(0, 10), timezone)}
                        {inv.due_date ? ` · due ${formatLocalDateInTz(inv.due_date.slice(0, 10), timezone)}` : ''}
                      </p>
                    </div>
                    <div className="text-right ml-auto">
                      <p className="text-sm text-neutral-600">
                        Total <span className="font-medium text-neutral-800">{money(inv.total_amount)}</span>
                      </p>
                      {!isVoid && (
                        <p className="text-sm text-neutral-600">
                          Still owing <span className="font-semibold text-neutral-900">{money(balance)}</span>
                        </p>
                      )}
                      <PaymentStateBadge state={invoicePaymentState(inv, today)} className="mt-1" />
                    </div>
                  </>
                );
                const rowCls = 'flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border border-neutral-100 rounded-lg px-4 py-3';
                return canAccounts ? (
                  <Link key={inv.id} to={`/accounts/invoices/${inv.id}`} className={`${rowCls} hover:border-primary-300`}>
                    {body}
                  </Link>
                ) : (
                  <div key={inv.id} className={rowCls}>
                    {body}
                    <div className="basis-full">
                      <Button variant="outline" size="sm" disabled={pdfBusy === inv.id} onClick={() => handleInvoicePdf(inv)}>
                        <Download className="h-4 w-4" />
                        {pdfBusy === inv.id ? 'Downloading…' : 'Download PDF'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {showReprice && (
          <RepriceOrderDialog
            orderId={order.id}
            orderNumber={order.order_number}
            onClose={() => setShowReprice(false)}
            onDone={async (result) => {
              setShowReprice(false);
              await loadOrder();
              const parts = [`${result.updated} line${result.updated === 1 ? '' : 's'} priced from today's price list.`];
              if (result.invoice_rebuilt) parts.push(`Draft invoice ${result.invoice_rebuilt} now carries the new prices.`);
              if (result.still_unpriced) parts.push(`${result.still_unpriced} still ha${result.still_unpriced === 1 ? 's' : 've'} no price.`);
              setRepriceNote(parts.join(' '));
            }}
          />
        )}

        {showCancel && (
          <CancelOrderDialog
            orderId={order.id}
            orderNumber={order.order_number}
            onClose={() => setShowCancel(false)}
            onCancelled={async (result) => {
              setShowCancel(false);
              // Reloading clears the page's error, so a failed refund is said after it.
              await loadOrder();
              if (result.refund?.status === 'failed') {
                setError(`The order is cancelled, but the refund did not go through: ${result.refund.error || 'no reason given'}`);
              }
            }}
          />
        )}

        {/* Allocate modal */}
        {showAllocate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-base font-semibold text-neutral-800 mb-1">Allocate Stock</h3>
              {allocLine && (
                <p className="text-sm text-neutral-500 mb-4 break-words">
                  {allocLine.product_name
                    ? `${allocLine.product_name} (any of its stock items)`
                    : [allocLine.cultivar_name, allocLine.rootstock_name, allocLine.tree_type_name || allocLine.tree_type_code].filter(Boolean).join(' / ')}
                  {' · '}{allocRemaining} of {allocLine.quantity_ordered} still to allocate
                </p>
              )}
              <div className="space-y-3">
                <div>
                  <label htmlFor="alloc-lot" className="block text-sm font-medium text-neutral-700 mb-1">Ready lot</label>
                  {availableStock === null ? (
                    <p className="text-sm text-neutral-400">Loading lots...</p>
                  ) : availableStock.length === 0 ? (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      {allocLine?.product_name
                        ? `No ready lots of any stock item ${allocLine.product_name} is filled from have stock left to allocate.`
                        : 'No ready lots of this cultivar, rootstock and tree type have stock left to allocate.'}
                      A lot has to be at the Ready stage before it can go on an order.
                    </p>
                  ) : (
                    <select
                      id="alloc-lot"
                      value={allocLotId}
                      onChange={e => handleSelectLot(e.target.value ? Number(e.target.value) : '')}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select lot...</option>
                      {availableStock.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.lot_code}
                          {allocLine?.product_name && s.cultivar_name
                            ? ` · ${[s.cultivar_name, s.rootstock_name].filter(Boolean).join(' / ')}`
                            : ''}
                          {s.location_name ? ` · ${s.location_name}` : ''} · {s.available_count} available
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label htmlFor="alloc-qty" className="block text-sm font-medium text-neutral-700 mb-1">Quantity</label>
                  <input
                    id="alloc-qty"
                    type="number"
                    min="1"
                    max={allocMax || undefined}
                    value={allocQuantity}
                    onChange={e => setAllocQuantity(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              {allocError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                  {allocError}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setShowAllocate(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAllocate}
                  disabled={!allocLotId || !allocQuantity || allocSubmitting}
                >
                  {allocSubmitting ? 'Allocating...' : 'Allocate'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice modal */}
        {showInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
              <h3 className="text-base font-semibold text-neutral-800 mb-4">Create Invoice</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={e => handleInvoiceDateChange(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={invoiceDueDate}
                    onChange={e => setInvoiceDueDate(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              {invoiceError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                  {invoiceError}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setShowInvoice(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreateInvoice}
                  disabled={!invoiceDate || invoiceSubmitting}
                >
                  {invoiceSubmitting ? 'Creating...' : 'Create Invoice'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function DetailRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <span className="text-neutral-500 min-w-[80px]">{label}</span>
      <span className="font-medium text-neutral-800 break-words min-w-0 flex-1">{children}</span>
    </div>
  );
}

export default OrderDetail;
